/** @jsxImportSource @emotion/react */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { VelocityTransitionGroup } from 'velocity-react';
import { css } from '@emotion/react';
import { TranslatedCopy } from '../intl/TranslatedCopy';

const subTextStyles = css`
  position: absolute;
  bottom: -12px;
  left: 0;
  padding-left: 18px;
  font-size: 12px;
  color: #111111;
`;

const newLabelStyles = css`
  padding-left: 5px;
  flex: 1;
  color: red;
  text-transform: uppercase;
  font-style: italic;
`;

class Collapsible extends Component {
  constructor(props) {
    super(props);

    this.state = { isOpen: props.open || false };
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentDidUpdate(prevProps) {
    const { open } = this.props;
    const { isOpen } = this.state;
    if (open !== prevProps.open && open !== isOpen) {
      this.setIsOpen();
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  setRef = node => (this.collapsibleRef = node);

  setIsOpen = () => {
    const { onClickCallback } = this.props;
    this.setState(prevState => ({ isOpen: !prevState.isOpen }));
    if (onClickCallback) onClickCallback();
  };

  handleClickOutside = event => {
    const { isOpen } = this.state;
    const { closeOnOutsideClick } = this.props;
    if (isOpen && this.collapsibleRef && closeOnOutsideClick) {
      if (!this.collapsibleRef.contains(event.target)) this.setIsOpen();
    }
  };

  render() {
    const {
      id,
      trigger,
      closeTrigger,
      classParentString,
      children,
      extraClassName,
      secondaryTrigger,
      subText,
      newLabel
    } = this.props;
    const { isOpen } = this.state;
    const classParent = classParentString ? `${classParentString}__` : '';
    const displayTrigger = typeof trigger === 'string' ? new TranslatedCopy({ id: trigger }).toString() : trigger;
    const transitionProps = {
      className: `${classParent}contentOuter`,
      enter: { animation: 'slideDown' },
      leave: { animation: 'slideUp' }
    };

    return (
      <div className={classnames(classParentString, { isOpen: isOpen }, extraClassName)} ref={this.setRef}>
        <div
          role='button'
          className={`${classParent}trigger body-3 u-bold`}
          onClick={this.setIsOpen}
          aria-expanded={isOpen}
          tabIndex='0'
          data-desc={`collapsible__${id}`}
          css={
            subText
              ? css`
                  position: relative;
                `
              : ''
          }
        >
          {isOpen && closeTrigger ? closeTrigger : displayTrigger}
          {secondaryTrigger && <span className='pl12-md pt4-sm pt0-md'>{secondaryTrigger}</span>}
          {subText && isOpen && (
            <div css={subTextStyles}>
              <TranslatedCopy id={subText} />
            </div>
          )}
          {newLabel && (
            <div css={newLabelStyles}>
              {'- '}
              <TranslatedCopy id='global.new' />
            </div>
          )}
        </div>
        <VelocityTransitionGroup
          {...transitionProps}
          css={
            subText
              ? css`
                  padding-top: 20px;
                `
              : ''
          }
        >
          {isOpen && <div className={`${classParent}contentInner body-3`}>{children}</div>}
        </VelocityTransitionGroup>
      </div>
    );
  }
}

Collapsible.propTypes = {
  children: PropTypes.any.isRequired,
  classParentString: PropTypes.string,
  closeOnOutsideClick: PropTypes.bool,
  closeTrigger: PropTypes.any,
  extraClassName: PropTypes.string,
  id: PropTypes.string.isRequired,
  open: PropTypes.bool,
  secondaryTrigger: PropTypes.any,
  trigger: PropTypes.any.isRequired,
  onClickCallback: PropTypes.func,
  subText: PropTypes.string,
  newLabel: PropTypes.bool
};

Collapsible.defaultProps = {
  classParentString: null,
  closeOnOutsideClick: false,
  closeTrigger: null,
  extraClassName: '',
  open: false,
  secondaryTrigger: null,
  onClickCallback: null,
  subText: null,
  newLabel: false
};

export default Collapsible;
